import React, { useState } from 'react';

export interface ResidenceUnit {
  id: string;
  name: string; // e.g. "Studios", "1 bed", "2 bed", "3 bed (upon request)"
  title?: string;
  area?: string; // e.g. "45 - 55 m²"
  description?: string;
  view_3d_image?: string;
  floor_plan_image?: string;
  images?: string[];
  features?: string[];
}

interface BrandResidencesProps {
  data?: {
    description?: string;
    units?: ResidenceUnit[];
  };
  onDownloadCatalogue?: () => void;
}

const DEFAULT_UNITS: ResidenceUnit[] = [
  {
    id: 'studios',
    name: 'Studios',
    title: 'Modern Luxury Studio Apartments',
    area: '48 m²',
    description: 'Intelligently planned urban residences offering maximized space, seamless flow and expansive balconies with breathtaking skyline views of Accra.',
    view_3d_image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1000&q=80',
    floor_plan_image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1000&q=80',
    images: [
      'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1000&q=80'
    ],
    features: ['Open-plan Living & Sleeping', 'Designer Kitchenette', 'Luxury Bathroom', 'Private Balcony']
  },
  {
    id: '1-bed',
    name: '1 bed',
    title: 'Sophisticated 1 Bedroom Residences',
    area: '72 m²',
    description: 'Generously proportioned one-bedroom suites designed for sophisticated modern living with bespoke finishes and panoramic Airport Residential vistas.',
    view_3d_image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1000&q=80',
    floor_plan_image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1000&q=80',
    images: [
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1000&q=80'
    ],
    features: ['Master Bedroom with En-suite', 'Spacious Living & Dining', 'Fully Fitted European Kitchen', 'Private Terrace']
  },
  {
    id: '2-bed',
    name: '2 bed',
    title: 'Prestigious 2 Bedroom Suites',
    area: '115 m²',
    description: 'Sprawling two-bedroom residences featuring expansive entertaining spaces, dual private balconies, and dedicated dressing suites.',
    view_3d_image: 'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1000&q=80',
    floor_plan_image: 'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1000&q=80',
    images: [
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1000&q=80'
    ],
    features: ['2 En-suite Bedrooms', 'Powder Room for Guests', 'Expansive Dining & Lounge', 'Wraparound Balcony']
  },
  {
    id: '3-bed',
    name: '3 bed (upon request)',
    title: 'Exclusive 3 Bedroom Penthouses (Upon Request)',
    area: '185 m²',
    description: 'Rare limited-edition luxury suites offering bespoke layouts, double-height ceilings and panoramic corner views of prime Accra.',
    view_3d_image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498f?auto=format&fit=crop&w=1000&q=80',
    floor_plan_image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498f?auto=format&fit=crop&w=1000&q=80',
    images: [
      'https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1000&q=80'
    ],
    features: ['3 Grand En-suite Bedrooms', 'Private Lift Access', 'Maid’s Quarters', 'Expansive Skyline Terrace']
  }
];

export const BrandResidences: React.FC<BrandResidencesProps> = ({
  data,
  onDownloadCatalogue
}) => {
  const introDescription =
    data?.description ||
    "Golden Tower is made up of attractive home types, befitting our ideal homeowners' investment style and portfolio.";

  const units = data?.units && data.units.length > 0 ? data.units : DEFAULT_UNITS;
  const [activeTabId, setActiveTabId] = useState(units[0]?.id || 'studios');
  const [viewMode, setViewMode] = useState<'3d' | 'photos'>('3d');
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);

  const currentUnit = units.find((u) => u.id === activeTabId) || units[0];
  const unit3DImage = currentUnit.view_3d_image || currentUnit.floor_plan_image;
  const unitPhotos = currentUnit.images || [];

  return (
    <section id="curated-residences" className="relative py-28 md:py-36 bg-[#0c0c12] text-white border-t border-white/5 overflow-hidden scroll-mt-10">
      <span id="residences" className="absolute -top-10" />
      {/* Background glow */}
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-[#C5A46D]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20 space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#C5A46D]/10 border border-[#C5A46D]/30 text-[#C5A46D] text-xs uppercase tracking-[0.25em] font-medium">
            Residence Types
          </div>
          <h2 className="text-3xl md:text-5xl font-light tracking-wide text-white">
            Curated <span className="font-semibold text-[#C5A46D]">Residences</span>
          </h2>
          <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#C5A46D] to-transparent mx-auto" />
          <p className="text-gray-300 text-base md:text-lg leading-relaxed font-light">
            {introDescription}
          </p>
        </div>

        {/* Tab Style Navigation */}
        <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 mb-12">
          {units.map((unit) => {
            const isActive = unit.id === activeTabId;
            return (
              <button
                key={unit.id}
                type="button"
                onClick={() => {
                  setActiveTabId(unit.id);
                  setActivePhotoIdx(0);
                }}
                className={`px-6 py-3.5 rounded-full text-xs md:text-sm uppercase tracking-[0.18em] font-medium transition-all duration-300 cursor-pointer border ${
                  isActive
                    ? 'bg-[#C5A46D] text-black border-[#C5A46D] shadow-lg shadow-[#C5A46D]/20 scale-105'
                    : 'bg-white/5 text-gray-300 border-white/10 hover:border-[#C5A46D]/50 hover:text-white hover:bg-white/10'
                }`}
              >
                {unit.name}
              </button>
            );
          })}
        </div>

        {/* Active Unit Content Card */}
        {currentUnit && (
          <div className="bg-[#12121a] border border-white/10 rounded-3xl p-6 md:p-10 lg:p-12 shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              
              {/* Left Info Column */}
              <div className="lg:col-span-5 space-y-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#C5A46D] font-semibold mb-2">
                    {currentUnit.name}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-light text-white leading-snug">
                    {currentUnit.title || currentUnit.name}
                  </h3>
                  {currentUnit.area && (
                    <div className="inline-block mt-3 px-3 py-1 bg-white/5 rounded-lg border border-white/10 text-sm text-[#C5A46D] font-medium">
                      Approx. {currentUnit.area}
                    </div>
                  )}
                </div>

                {currentUnit.description && (
                  <p className="text-gray-300 text-sm md:text-base leading-relaxed font-light">
                    {currentUnit.description}
                  </p>
                )}

                {/* Features checklist */}
                {currentUnit.features && currentUnit.features.length > 0 && (
                  <div className="space-y-2.5 pt-4 border-t border-white/10">
                    <div className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2">
                      Key Highlights
                    </div>
                    {currentUnit.features.map((feat, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-sm text-gray-300">
                        <i className="ri-checkbox-circle-fill text-[#C5A46D] text-base shrink-0"></i>
                        <span>{feat}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Actions & Catalogue Button */}
                <div className="pt-6 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
                  <button
                    type="button"
                    onClick={onDownloadCatalogue}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#C5A46D] hover:bg-[#b8955e] text-black font-semibold rounded-full text-xs md:text-sm tracking-[0.18em] uppercase transition-all duration-300 shadow-lg shadow-[#C5A46D]/20 cursor-pointer"
                  >
                    <i className="ri-file-download-line text-lg"></i>
                    Download Catalogue
                  </button>

                  {/* Toggle 3D View / Photos */}
                  <div className="inline-flex rounded-full bg-white/5 border border-white/10 p-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => setViewMode('3d')}
                      className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-all cursor-pointer ${
                        viewMode === '3d'
                          ? 'bg-[#C5A46D] text-black font-semibold shadow-md'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      3D View
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setViewMode('photos');
                        setActivePhotoIdx(0);
                      }}
                      className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-all cursor-pointer ${
                        viewMode === 'photos'
                          ? 'bg-[#C5A46D] text-black font-semibold shadow-md'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Photos
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Visual Display Column */}
              <div className="lg:col-span-7">
                <div className="relative rounded-2xl overflow-hidden bg-black/60 border border-white/10 aspect-[4/3] md:aspect-[16/10] flex items-center justify-center">
                  {viewMode === '3d' ? (
                    unit3DImage ? (
                      <img
                        src={unit3DImage}
                        alt={`${currentUnit.name} 3D View`}
                        className="w-full h-full object-cover filter contrast-105"
                      />
                    ) : (
                      <div className="text-center text-gray-500 space-y-2 p-8">
                        <i className="ri-layout-masonry-line text-5xl opacity-40"></i>
                        <p className="text-sm">3D View will be uploaded soon</p>
                      </div>
                    )
                  ) : (
                    unitPhotos.length > 0 ? (
                      <div className="relative w-full h-full group">
                        <img
                          src={unitPhotos[activePhotoIdx] || unitPhotos[0]}
                          alt={`${currentUnit.name} Visual`}
                          className="w-full h-full object-cover"
                        />

                        {/* Navigation controls if multiple photos */}
                        {unitPhotos.length > 1 && (
                          <>
                            <button
                              type="button"
                              onClick={() => setActivePhotoIdx((prev) => (prev - 1 + unitPhotos.length) % unitPhotos.length)}
                              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors cursor-pointer border border-white/20 shadow-lg"
                              title="Previous Photo"
                            >
                              <i className="ri-arrow-left-s-line text-xl"></i>
                            </button>
                            <button
                              type="button"
                              onClick={() => setActivePhotoIdx((prev) => (prev + 1) % unitPhotos.length)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors cursor-pointer border border-white/20 shadow-lg"
                              title="Next Photo"
                            >
                              <i className="ri-arrow-right-s-line text-xl"></i>
                            </button>

                            {/* Dots Indicator */}
                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10">
                              {unitPhotos.map((_, pIdx) => (
                                <button
                                  key={pIdx}
                                  type="button"
                                  onClick={() => setActivePhotoIdx(pIdx)}
                                  className={`h-2 rounded-full transition-all cursor-pointer ${
                                    activePhotoIdx === pIdx ? 'bg-[#C5A46D] w-6' : 'bg-white/40 hover:bg-white/80 w-2'
                                  }`}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 space-y-2 p-8">
                        <i className="ri-image-line text-5xl opacity-40"></i>
                        <p className="text-sm">Photos will be uploaded soon</p>
                      </div>
                    )
                  )}

                  {/* Badge */}
                  <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/10 text-[11px] uppercase tracking-wider text-gray-200 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#C5A46D]"></span>
                    {viewMode === '3d'
                      ? '3D View'
                      : `Photo ${activePhotoIdx + 1} of ${unitPhotos.length || 1}`}
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default BrandResidences;
